import React, { useState, useEffect } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import { VscOpenPreview, VscGraph } from "react-icons/vsc";
import { FaEdit, FaExpandAlt } from "react-icons/fa";
import DrawingOverlay from "./DrawingOverlay";
import GraphModal from "./GraphModal";
import socket from "./socket";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

function Attendance({ expanded, setExpanded, selectedYear, selectedQuarter }) {
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentUserName, setCurrentUserName] = useState("");
  const [attendanceFullData, setAttendanceFullData] = useState({
    current: [],
    previous: [],
  }); // full data for modal
  const [attendanceData, setAttendanceData] = useState({
    current: [],
    previous: [],
  });
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(true);
  const [attendanceByLevel, setAttendanceByLevel] = useState({
    current: [],
    previous: [],
  });
  const [isLevelLoading, setIsLevelLoading] = useState(true);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [maxAttendance, setMaxAttendance] = useState(null);
  const [minAttendance, setMinAttendance] = useState(null);
  const [attendanceBubbleCounts, setAttendanceBubbleCounts] = useState(null);
  const [under90AttendanceByLevelList, setUnder90AttendanceByLevelList] =
    useState([]);

  const itemsPerPage = 7;
  const [currentPageAtt, setCurrentPageAtt] = useState(1);
  const totalPagesAtt = Math.ceil(
    under90AttendanceByLevelList.length / itemsPerPage
  );
  const paginatedLevels = under90AttendanceByLevelList.slice(
    (currentPageAtt - 1) * itemsPerPage,
    currentPageAtt * itemsPerPage
  );

  const navigate = useNavigate();

  const handleViewAttendance = () => {
    const currentQuarter = `Q${selectedQuarter} ${selectedYear}`;
    const previousQuarter =
      selectedQuarter === 1
        ? `Q4 ${selectedYear - 1}`
        : `Q${selectedQuarter - 1} ${selectedYear}`;

    navigate("/detailattendance", {
      state: {
        selectedQuarters: [currentQuarter, previousQuarter],
        currentData: attendanceFullData.current,
        previousData: attendanceFullData.previous,
      },
    });
  };

  // Socket Connection
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));

    const registerUser = () => {
      if (storedUser) {
        socket.emit("register", storedUser.user_name);
        setCurrentUserName(storedUser.user_name);
      }
    };

    socket.connect();
    registerUser();

    socket.on("connect", registerUser);

    const handleUsersUpdate = (users) => {
      setUsers(users);
    };

    socket.on("updateUsers", handleUsersUpdate);

    return () => {
      socket.off("updateUsers", handleUsersUpdate);
      socket.off("connect", registerUser);
    };
  }, []);

  const convertMergedAttendance = (current, previous) => {
    const currentMap = new Map();
    const previousMap = new Map();
    const levelMap = new Map();

    current.forEach((item) => {
      currentMap.set(item.course_code, parseFloat(item.course_att_rate));
      levelMap.set(item.course_code, item.course_level || "Uncategorized");
    });

    previous.forEach((item) => {
      previousMap.set(item.course_code, parseFloat(item.course_att_rate));
      if (!levelMap.has(item.course_code)) {
        levelMap.set(item.course_code, item.course_level || "Uncategorized");
      }
    });

    const allLabels = current.map((item) => item.course_code);

    const merged = allLabels.map((label) => ({
      label,
      current: currentMap.get(label) || 0,
      previous: previousMap.get(label) || 0,
      course_level: levelMap.get(label) || "Uncategorized",
    }));

    return merged;
  };

  const alignAttendanceByLevel = (current = [], previous = []) => {
    const previousMap = new Map();
    previous.forEach((item) => {
      const label = (item.level_name || "").trim();
      previousMap.set(label, parseFloat(item.level_att_rate));
    });

    return current.map((item) => {
      const label = (item.level_name || "").trim();
      return {
        label,
        current: parseFloat(item.level_att_rate),
        previous: previousMap.get(label) || 0,
      };
    });
  };

  // Frontend and Backend Integration
  useEffect(() => {
    const fetchAttendance = async () => {
      setIsAttendanceLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getAttendanceByCourse?qtr=${selectedQuarter}&year=${selectedYear}`
        );
        const { success, current, previous } = res.data;

        if (success) {
          const merged = convertMergedAttendance(current || [], previous || []);

          const fullCurrent = merged.map((d) => ({
            label: d.label,
            y: d.current,
          }));

          const fullPrevious = merged.map((d) => ({
            label: d.label,
            y: d.previous,
          }));

          setAttendanceFullData({
            current: fullCurrent,
            previous: fullPrevious,
          });

          const top10 = [...merged]
            .sort((a, b) => b.current - a.current)
            .slice(0, 10);

          setAttendanceData({
            current: top10.map((d) => ({ label: d.label, y: d.current })),
            previous: top10.map((d) => ({ label: d.label, y: d.previous })),
          });

          const max = fullCurrent.reduce(
            (prev, curr) => (curr.y > prev.y ? curr : prev),
            { y: 0 }
          );
          const min = fullCurrent.reduce(
            (prev, curr) => (curr.y < prev.y ? curr : prev),
            { y: 100 }
          );

          const under90ByLevelMap = {};

          (current || []).forEach((item) => {
            const rate = Number(item.att_rate ?? 0);
            if (rate < 90) {
              const level = item.course_level?.trim() || "Uncategorized";
              under90ByLevelMap[level] = (under90ByLevelMap[level] || 0) + 1;
            }
          });

          const under90ByLevelList = Object.entries(under90ByLevelMap)
            .map(([level, count]) => ({ level, count }))
            .sort((a, b) => b.count - a.count);

          setUnder90AttendanceByLevelList(under90ByLevelList);
          setMaxAttendance(max);
          setMinAttendance(min);
        }
      } catch (err) {
        console.error("Failed to fetch attendance data", err);
      } finally {
        setIsAttendanceLoading(false);
      }
    };

    const fetchAttendanceByLevel = async () => {
      setIsLevelLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getAttendanceByLevel?qtr=${
            [1, 2, 3, 4].includes(Number(selectedQuarter)) ? selectedQuarter : 1
          }&year=${selectedYear}`
        );
        const { success, current, previous } = res.data;

        if (success) {
          const aligned = alignAttendanceByLevel(current, previous);

          setAttendanceByLevel({
            current: aligned.map((d) => ({ label: d.label, y: d.current })),
            previous: aligned.map((d) => ({ label: d.label, y: d.previous })),
          });
        }
      } catch (err) {
        console.error("Failed to fetch attendance by level", err);
      } finally {
        setIsLevelLoading(false);
      }
    };

    const fetchAttendanceByStudent = async () => {
      setAttendanceBubbleCounts(null);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getAttendanceByStudent?qtr=${selectedQuarter}&year=${selectedYear}`
        );
        const { success, current } = res.data;
        if (success) {
          const counts = {
            range0_30: 0, // 0–29
            range30_40: 0, // 30–39
            range40_60: 0, // 40–59
            range60_85: 0, // 60–84
            range85_100: 0, // 85–100
          };

          current.forEach((item) => {
            const rate = Number(item.att_rate);

            if (rate < 30) {
              counts.range0_30++;
            } else if (rate >= 30 && rate < 40) {
              counts.range30_40++;
            } else if (rate >= 40 && rate < 60) {
              counts.range40_60++;
            } else if (rate >= 60 && rate < 85) {
              counts.range60_85++;
            } else if (rate >= 85) {
              counts.range85_100++;
            }
          });

          setAttendanceBubbleCounts(counts);
        }
      } catch (err) {
        console.error("Failed to fetch attendance by student", err);
      }
    };

    fetchAttendance();
    fetchAttendanceByLevel();
    fetchAttendanceByStudent();
  }, [selectedQuarter, selectedYear]);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const headerStyle = {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  };

  const chartBoxStyle = {
    backgroundColor: "#2f2f2f",
    borderRadius: 10,
    padding: 20,
    minWidth: 300,
    height: "300px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  const iconStyle = {
    position: "absolute",
    top: 8,
    right: 15,
    color: "#bbb",
    cursor: "pointer",
  };

  const highLowBox = {
    backgroundColor: "#2C2C2C",
    borderRadius: 10,
    padding: 20,
    // flex: 1,
    boxShadow:
      "inset 0 2px 6px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(255, 255, 255, 0.05)",
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#373636",
        margin: 20,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h5 style={{ color: "#fff", margin: 0 }}>Attendance Rate</h5>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {users.map((user) => {
              return (
                <div
                  key={user.id}
                  title={user.name}
                  style={{
                    position: "relative", // Needed to position the green dot
                    width: 32,
                    height: 32,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      backgroundColor: stringToColor(user.name),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: "bold",
                      color: "#fff",
                    }}
                  >
                    {user.name[0].toUpperCase()}
                  </div>

                  {user.name === currentUserName && isDrawingMode && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#4CAF50",
                        border: "2px solid white",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <FaEdit
            size={20}
            style={{ color: "#fff", cursor: "pointer" }}
            onClick={() => setIsDrawingMode(!isDrawingMode)}
          />
          <FaExpandAlt
            size={20}
            onClick={handleToggleExpand}
            style={{ color: "#fff", cursor: "pointer" }}
            title={expanded ? "Collapse" : "Expand"}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minWidth: 250,
            height: "400px",
          }}
        >
          <div style={{ ...highLowBox, flex: 1, marginTop: "2px" }}>
            <div style={{ color: "#aaa", fontSize: 14, marginBottom: 28 }}>
              Attendance Rate (High - Low)
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{ fontSize: 25, color: "#4CAF50", fontWeight: "bold" }}
                >
                  {maxAttendance ? `${maxAttendance.y.toFixed(2)}%` : "-"}
                </div>
                <div style={{ fontSize: 12, color: "#ccc" }}>
                  {maxAttendance ? maxAttendance.label : "-"}
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <div
                  style={{ fontSize: 25, color: "#F44336", fontWeight: "bold" }}
                >
                  {minAttendance ? `${minAttendance.y.toFixed(2)}%` : "-"}
                </div>
                <div style={{ fontSize: 12, color: "#ccc" }}>
                  {minAttendance ? minAttendance.label : "-"}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              ...highLowBox,
              minHeight: "465px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ color: "#aaa", fontSize: 14 }}>
                Attendance &lt; 90% by Level
              </span>
              <span
                style={{
                  backgroundColor: "#555",
                  color: "#aaa",
                  fontWeight: "bold",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 13,
                  display: "inline-block",
                  minWidth: 28,
                  textAlign: "center",
                }}
              >
                {under90AttendanceByLevelList.length}
              </span>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
                marginTop: "10px",
              }}
            >
              {paginatedLevels.length > 0 ? (
                paginatedLevels.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ color: "#fff", fontSize: 14 }}>
                      {item.level}
                    </span>
                    <span
                      style={{
                        backgroundColor: "#FFA726",
                        color: "#000",
                        fontWeight: "bold",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                      }}
                    >
                      {item.count}
                    </span>
                  </div>
                ))
              ) : (
                <span style={{ color: "#aaa", fontSize: 13 }}>
                  No attendance data under 90%
                </span>
              )}

              {totalPagesAtt > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                  }}
                >
                  <button
                    onClick={() =>
                      setCurrentPageAtt((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPageAtt === 1}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: "#555",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <IoIosArrowBack size={18} />
                  </button>
                  <span style={{ color: "#fff", fontSize: 12, marginTop: 8 }}>
                    Page {currentPageAtt} of {totalPagesAtt}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPageAtt((prev) =>
                        Math.min(prev + 1, totalPagesAtt)
                      )
                    }
                    disabled={currentPageAtt === totalPagesAtt}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: "#555",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <IoIosArrowForward size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Bar Charts */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div style={chartBoxStyle}>
            <div style={iconStyle}>
              <div style={{ ...iconStyle, display: "flex", gap: 10 }}>
                <div title="View Attendance Chart">
                  <VscGraph
                    size={18}
                    onClick={() => setShowAttendanceModal(true)}
                    style={{ cursor: "pointer" }}
                  />
                </div>
                <div title="View Attendance data">
                  <VscOpenPreview size={18} onClick={handleViewAttendance} />
                </div>
              </div>
            </div>
            <div style={headerStyle}>ATTENDANCE RATE</div>
            {isAttendanceLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 220,
                }}
              >
                <div className="spinner" />
              </div>
            ) : (
              <CanvasJSChart
                options={{
                  height: 220,
                  animationEnabled: true,
                  backgroundColor: "#2f2f2f",
                  axisX: {
                    labelFontColor: "#ccc",
                    title: "Courses",
                    titleFontColor: "#ccc",
                    labelAngle: -45,
                    labelFontSize: 9,
                  },
                  axisY: {
                    labelFontColor: "#ccc",
                    suffix: "%",
                    title: "Attendance Rate",
                    titleFontColor: "#ccc",
                    interval: 20,
                    maximum: 100,
                  },
                  toolTip: { shared: true },
                  legend: { cursor: "pointer", fontColor: "#fff" },
                  data: [
                    {
                      type: "column",
                      name: `Q${selectedQuarter} ${selectedYear}`,
                      showInLegend: true,
                      color: "#5D5FEF",
                      dataPoints: attendanceData.current,
                    },
                    {
                      type: "column",
                      name: `Q${
                        selectedQuarter === 1 ? 4 : selectedQuarter - 1
                      } ${
                        selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                      }`,
                      showInLegend: true,
                      color: "#8884d8",
                      dataPoints: attendanceData.previous,
                    },
                  ],
                }}
              />
            )}
          </div>

          <div style={chartBoxStyle}>
            <div style={headerStyle}>ATTENDANCE RATE TREND (Course Level)</div>
            {isLevelLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 220,
                }}
              >
                <div className="spinner" />
              </div>
            ) : (
              <CanvasJSChart
                options={{
                  height: 220,
                  animationEnabled: true,
                  backgroundColor: "#2f2f2f",
                  axisY: {
                    suffix: "%",
                    labelFontColor: "#ccc",
                    title: "Attendance Rate",
                    titleFontColor: "#ccc",
                    interval: 20,
                    maximum: 100,
                  },
                  axisX: {
                    labelFontColor: "#ccc",
                    title: "Course Levels",
                    titleFontColor: "#ccc",
                    labelAngle: -20,
                    labelFontSize: 10,
                  },
                  toolTip: { shared: true },
                  legend: { cursor: "pointer", fontColor: "#fff" },
                  data: [
                    {
                      type: "line",
                      name: `Q${selectedQuarter} ${selectedYear}`,
                      showInLegend: true,
                      color: "#f9a825",
                      indexLabel: "{y}%",
                      indexLabelFontColor: "#fff",
                      indexLabelFontSize: 10,
                      dataPoints: attendanceByLevel.current,
                    },
                    {
                      type: "line",
                      name: `Q${
                        selectedQuarter === 1 ? 4 : selectedQuarter - 1
                      } ${
                        selectedQuarter === 1 ? selectedYear - 1 : selectedYear
                      }`,
                      showInLegend: true,
                      color: "#8884d8",
                      dataPoints: attendanceByLevel.previous,
                    },
                  ],
                }}
              />
            )}
          </div>
        </div>

        {/* Column 3: percentage data */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 2,
            minWidth: 350,
          }}
        >
          <div
            style={{ ...chartBoxStyle, height: "620px", position: "relative" }}
          >
            <div style={headerStyle}>ATTENDANCE BY PERCENTAGE</div>

            {attendanceBubbleCounts ? (
              (() => {
                const bubbleStyle = {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: "bold",
                  borderRadius: "50%",
                  width: 120,
                  height: 120,
                  fontSize: 14,
                  textAlign: "center",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
                };

                return (
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        ...bubbleStyle,
                        backgroundColor: "#388E3C",
                        width: 200,
                        height: 200,
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <div>85%-100%</div>
                      <div style={{ fontSize: 50 }}>
                        {attendanceBubbleCounts.range85_100}
                      </div>
                      <div style={{ fontSize: 12 }}>student count</div>
                    </div>

                    <div
                      style={{
                        ...bubbleStyle,
                        backgroundColor: "#1976D2",
                        position: "absolute",
                        top: "20%",
                        right: "10%",
                      }}
                    >
                      <div> 60% - 85%</div>
                      <div style={{ fontSize: 30 }}>
                        {attendanceBubbleCounts.range60_85}
                      </div>
                      <div style={{ fontSize: 10 }}>student count</div>
                    </div>

                    <div
                      style={{
                        ...bubbleStyle,
                        backgroundColor: "#8E24AA",
                        position: "absolute",
                        top: "20%",
                        left: "10%",
                      }}
                    >
                      <div> 40% - 60%</div>
                      <div style={{ fontSize: 30 }}>
                        {attendanceBubbleCounts.range40_60}
                      </div>
                      <div style={{ fontSize: 10 }}>student count</div>
                    </div>

                    <div
                      style={{
                        ...bubbleStyle,
                        backgroundColor: "#F4511E",
                        position: "absolute",
                        bottom: "15%",
                        right: "15%",
                      }}
                    >
                      <div> 30% - 40%</div>
                      <div style={{ fontSize: 30 }}>
                        {attendanceBubbleCounts.range30_40}
                      </div>
                      <div style={{ fontSize: 10 }}>student count</div>
                    </div>

                    <div
                      style={{
                        ...bubbleStyle,
                        backgroundColor: "#D32F2F",
                        position: "absolute",
                        bottom: "15%",
                        left: "15%",
                      }}
                    >
                      <div> 0 - 30%</div>
                      <div style={{ fontSize: 30 }}>
                        {attendanceBubbleCounts.range0_30}
                      </div>
                      <div style={{ fontSize: 10 }}>student count</div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div style={{ color: "#aaa", fontSize: 14 }}>
                Loading attendance data...
              </div>
            )}
          </div>
        </div>
      </div>

      <GraphModal
        show={showAttendanceModal}
        onClose={() => setShowAttendanceModal(false)}
        title="ATTENDANCE RATE COMPARISON"
        chartOptions={{
          height: 600,
          animationEnabled: true,
          backgroundColor: "#2f2f2f",
          axisX: {
            title: "Courses",
            labelFontColor: "#ccc",
            titleFontColor: "#ccc",
            reversed: true,
            interval: 1,
            labelFontSize: 12,
          },
          axisY: {
            title: "Attendance Rate (%)",
            labelFontColor: "#ccc",
            titleFontColor: "#ccc",
            gridColor: "#444",
            interval: 10,
          },
          toolTip: { shared: true },
          legend: { cursor: "pointer", fontColor: "#fff" },
          data: [
            {
              type: "bar",
              name: `Q${selectedQuarter === 1 ? 4 : selectedQuarter - 1} ${
                selectedQuarter === 1 ? selectedYear - 1 : selectedYear
              }`,
              showInLegend: true,
              color: "#8884d8",
              dataPoints: attendanceFullData.previous,
            },
            {
              type: "bar",
              name: `Q${selectedQuarter} ${selectedYear}`,
              showInLegend: true,
              color: "#5D5FEF",
              dataPoints: attendanceFullData.current,
            },
          ],
        }}
      />

      {isDrawingMode && (
        <DrawingOverlay onClose={() => setIsDrawingMode(false)} />
      )}
    </div>
  );
}

export default Attendance;
